import React, { useEffect, useState } from "react";
import {
  Grid,
  Card,
  Typography,
  Box,
  CardContent,
  Paper,
  styled,
} from "@mui/material";
import { io } from "socket.io-client";
import { baseUrlMasterAdmin } from "@api/const";
import CpuIcon from "../../../../../assets/icons/Icon-cpu.svg";
import MemoryIcon from "../../../../../assets/icons/Icon-memory-2.svg";
import StorageIcon from "../../../../../assets/icons/Icon-storage.svg";
import DataGrid from "@components/shared/DataGrid/DataGrid";
import { GridColDef } from "@mui/x-data-grid";

const StyledCard = styled(Card)(({ theme }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  boxShadow: "none",
  border: "none",
}));

const StatisticCard = ({ title, value, icon, color, trend }) => (
  <StyledCard>
    <CardContent sx={{ p: 2, flexGrow: 1 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        <Typography
          variant="h6"
          component="div"
          color={color}
          fontWeight="bold"
        >
          {value}
        </Typography>
        <Box
          component="img"
          src={icon}
          alt={title}
          sx={{ width: 44, height: 44 }}
        />
      </Box>
      <Typography variant="body2" color="text.secondary" fontWeight={300}>
        {title}
      </Typography>
      {trend && (
        <Typography variant="caption" color="text.secondary">
          {trend}
        </Typography>
      )}
    </CardContent>
  </StyledCard>
);

const Dashboard = () => {
  const [socketAnswer, setSocketAnswer] = useState(null);

  useEffect(() => {
    const socket = io(baseUrlMasterAdmin, {
      extraHeaders: { Origin: ":" },
    });

    socket.emit("getMetrics");
    socket.on("metrics", (answer) => {
      setSocketAnswer(answer);
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  const monitoringCards = [
    {
      title: "CPU Usage",
      value: (socketAnswer && socketAnswer?.cpu_usage?.replace("%", "")) || 0,
      suffix: "%",
      icon: CpuIcon,
      color: "#000",
      trend: "+01% this week",
    },
    {
      title: "Memory Usage",
      value:
        (socketAnswer && socketAnswer?.memory_usage?.replace("%", "")) || 0,
      suffix: "%",
      icon: MemoryIcon,
      color: "#ff4d4f",
      trend: "+101% this week",
    },
    {
      title: "Storage In Use",
      value:
        (socketAnswer && socketAnswer?.storage_usage?.replace("%", "")) || 0,
      suffix: "%",
      icon: StorageIcon,
      color: "#52c41a",
      trend: "-01% this week",
    },
  ];

  const columns: GridColDef[] = [
    {
      field: "resource",
      headerName: "Resources",
      flex: 2,
      headerAlign: "left",
      align: "left",
      headerClassName: "data-grid-header",
      cellClassName: "data-grid-cell",
    },
    {
      field: "utilization",
      headerName: "Utilization",
      flex: 1,
      headerAlign: "center",
      align: "center",
      headerClassName: "data-grid-header",
      cellClassName: "data-grid-cell",
    },
    {
      field: "description",
      headerName: "Description",
      flex: 3,
      headerAlign: "left",
      align: "left",
      headerClassName: "data-grid-header",
      cellClassName: "data-grid-cell",
      renderCell: (params) => (
        <Box
          sx={{
            width: "100%",
            py: 1.5,
            color: "text.secondary",
            fontSize: "0.875rem",
            lineHeight: 1.5,
          }}
        >
          {params.value}
        </Box>
      ),
    },
  ];

  const tableData = [
    {
      id: 1,
      resource: "Total tokens remaining",
      utilization: "--",
      description:
        "Log in to our Open AI account to view our usage tracking dashboard. This page will show you how many tokens CFC has used during the current and past billing cycles.",
    },
    {
      id: 2,
      resource: "Utilization rate",
      utilization: "--",
      description:
        "The percentage of the network's total bandwidth currently being used. It helps in identifying potential bottlenecks.",
    },
    {
      id: 3,
      resource: "Network throughput",
      utilization: "--",
      description:
        "This indicates the volume of data a network can handle in a given time and is often measured in bits per second (bps) or packets per second (pps).",
    },
    {
      id: 4,
      resource: "Network latency",
      utilization: "--",
      description:
        "Measures the time it takes for a packet of data to get from the source to the destination. High latency can result in noticeable delays in network communication.",
    },
    {
      id: 5,
      resource: "Connection establishment time",
      utilization: "--",
      description:
        "Measures the time taken to establish a connection between two network nodes.",
    },
  ];

  return (
    <div style={{ padding: "20px" }}>
      {/* Monitoring Section */}
      <Typography
        variant="h5"
        sx={{
          borderBottom: "1px solid #E0E0E0",
          paddingBottom: 2,
          margin: "32px 0 24px 0", // Increased top margin to 32px for better spacing with new header
          fontWeight: 600,
          fontSize: "20px",
          lineHeight: "28px",
        }}
      >
        Monitoring
      </Typography>
      <Paper
        elevation={0}
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          mb: 4,
          backgroundColor: "background.paper",
          borderRadius: 1,
          overflow: "hidden",
          height: 160,
        }}
      >
        {monitoringCards.map((card, index) => (
          <React.Fragment key={index}>
            <Box
              sx={{
                flex: 1,
                p: 3,
                position: "relative",
                "&:not(:last-child)::after": {
                  content: '""',
                  position: "absolute",
                  right: 0,
                  top: "50%",
                  transform: "translateY(-50%)",
                  width: "1px",
                  height: "60%",
                  backgroundColor: "divider",
                  display:
                    index === monitoringCards.length - 1
                      ? "none"
                      : { xs: "none", md: "block" },
                },
              }}
            >
              <StatisticCard
                title={card.title}
                value={`${card.value}${card.suffix}`}
                icon={card.icon}
                color={card.color}
                trend={card.trend}
              />
            </Box>
          </React.Fragment>
        ))}
      </Paper>

      {/* Performance Insights Table */}
      <Typography variant="h6" sx={{ mt: 5, mb: 2, fontWeight: 600 }}>
        Performance Insights
      </Typography>
      <Box sx={{ height: "calc(100vh - 400px)", width: "100%", mt: 2 }}>
        <DataGrid
          headerBgColor="#F7F7F7"
          columns={columns}
          rows={tableData}
          loading={!socketAnswer}
          initialState={{
            pagination: {
              paginationModel: {
                page: 0,
                pageSize: tableData.length <= 10 ? 100 : 20,
              },
            },
          }}
          pageSizeOptions={tableData.length > 10 ? [10, 20, 50, 100] : []}
          hideFooter={tableData.length <= 10}
        />
      </Box>
    </div>
  );
};

export default Dashboard;
